import { useState } from 'react';
import { Err } from '../ui.js';
import { resetPassword, ApiError, setSecret } from '../api.js';
import type { Identity } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// THE SCREEN AT THE END OF A RESET LINK.
//
// Rendered BEFORE the sign-in gate in App.tsx, and that is the whole trick:
// somebody following this link cannot sign in by definition, so a screen that
// sits behind the gate is a screen they can never reach.
//
// IT SIGNS THEM IN ON SUCCESS. The alternative — "password changed, now go and
// sign in" — asks a person who has just proved they could not remember their
// password to type it again immediately. The reset endpoint already mints a
// session for exactly this reason.
//
// THE TOKEN IS NEVER SHOWN OR ECHOED. It is a live credential until it is
// spent; putting it on screen invites a screenshot, and putting it in an error
// message puts it in a support thread.
// ─────────────────────────────────────────────────────────────────────────────

export function ResetPassword({ onIn }: { onIn: (id: Identity & { token: string }) => void }) {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Checked here as well as on the server, because a mismatch is the one
  // mistake the server genuinely cannot catch — it only ever sees one of them.
  const mismatch = confirm.length > 0 && password !== confirm;
  const ready = password.length >= 10 && !mismatch && !busy;

  async function go() {
    setBusy(true); setError(null);
    try {
      const id = await resetPassword(token, password);
      setSecret(id.token);
      onIn(id);
      // Drop the token out of the address bar before anything else renders, so
      // it does not sit in history or get pasted into a bug report along with
      // the URL.
      window.history.replaceState({}, '', '/');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="signin">
        <div className="signincard">
          <h1>That link is incomplete</h1>
          <p className="fieldhint">
            The address has no reset token in it — mail clients sometimes break a
            long link across lines. Ask for a new one from the sign-in screen.
          </p>
          <div className="actions">
            <button className="primary" onClick={() => { window.location.href = '/'; }}>
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signin">
      <form className="signincard" onSubmit={(e) => { e.preventDefault(); if (ready) void go(); }}>
        <h1>Choose a new password</h1>
        <p className="fieldhint">
          This link works once. Setting a new password ends every other sign-in
          on your account.
        </p>

        <Err>{error}</Err>

        <label className="field">
          <span className="field-label">New password</span>
          <input type="password" value={password} autoFocus autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)} />
        </label>

        <label className="field">
          <span className="field-label">Type it again</span>
          <input type="password" value={confirm} autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)} />
        </label>

        {mismatch && <p className="fieldhint">Those two do not match.</p>}
        {!mismatch && password.length > 0 && password.length < 10 && (
          <p className="fieldhint">Ten characters or more.</p>
        )}

        <div className="actions">
          <button className="primary" type="submit" disabled={!ready}>
            {busy ? 'Setting it…' : 'Set password and sign in'}
          </button>
        </div>
      </form>
    </div>
  );
}
